import { useParams, useSearchParams } from 'react-router-dom'

import { F0Alert } from '@factorialco/f0-react'
import { Box } from '@factorialco/deprecated-design-system'
import {
  FinanceBudgetDetailRevampPageDocument,
  TrainingsTabsDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { amountWithCurrency } from 'modules/finance/lib/budgets'
import { EditTrainingBudgetDialog } from 'modules/trainings/components/Budgets/EditTrainingBudgetDialog'
import { TrainingResourceDataCollection } from 'modules/trainings/components/Budgets/TrainingResourceDataCollection'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

const BudgetDetail = () => {
  const { budgetId } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const {
    access: { locale },
    company,
  } = useCurrentSession()

  const { data: tabsData } = useQuery(TrainingsTabsDocument, {
    variables: { companyId: company.id },
  })
  const canManageTrainingBudgets = tabsData?.finance?._policies?.budgetsManageTrainingBudgets?.can

  const { data, loading, error, refetch } = useQuery(FinanceBudgetDetailRevampPageDocument, {
    variables: {
      budgetId: budgetId ?? '',
    },
  })
  const budget = data?.finance?.budget
  const amounts = data?.finance?.budgetAmountsConnection?.nodes[0]

  const isEditOpen = searchParams.get('modal') === 'edit'

  const handleEditClose = () => {
    setSearchParams({})
  }

  const handleEditSuccess = async () => {
    await refetch()
    setSearchParams({})
  }

  const showOverBudgetAlert =
    budget && amounts
      ? budget.currentStatus === 'over_budget' && amounts.budgetedAmountCents > 0
      : false
  const exceedAmount =
    showOverBudgetAlert && amounts && budget
      ? amountWithCurrency(Math.abs(amounts.availableAmountCents), budget.currency, locale)
      : 0
  const exceedPercentage =
    showOverBudgetAlert && amounts
      ? (Math.abs(amounts.committedAmountCents) * 100) / amounts.budgetedAmountCents
      : 0

  return (
    <ResponsivePage.Body loading={loading} error={error}>
      {budget && amounts && (
        <Box flexDirection="column" gap="s16" height="full">
          {showOverBudgetAlert && (
            <F0Alert
              description={i18n.t('budgets.overbudget_alert.description', {
                exceed_amount: exceedAmount,
                exceed_percentage: exceedPercentage.toFixed(2),
              })}
              title={i18n.t('budgets.overbudget_alert.title')}
              variant="critical"
            />
          )}
          <TrainingResourceDataCollection
            locale={locale}
            budget={budget}
            amounts={amounts}
            canManage={canManageTrainingBudgets}
          />
        </Box>
      )}
      {isEditOpen && budget && canManageTrainingBudgets && (
        <EditTrainingBudgetDialog
          budget={budget}
          onClose={handleEditClose}
          onSuccess={handleEditSuccess}
        />
      )}
    </ResponsivePage.Body>
  )
}

export default BudgetDetail
